<template>
  <modal
    name="modal--edit-product"
    transition="nice-modal-fade"
    :click-to-close="false"
    :width="350"
    :height="'auto'"
    :adaptive="true"
    :reset="true"
    :scrollable="true"
    draggable=".drag-handler"
    @before-open="beforeOpen"
    @before-close="beforeClose"
    class="py-10"
  >
    <div
      class="flex flex-col text-black h-full bg-white overflow-hidden w-full justify-between p-4"
    >
      <div
        class="text-center text-4xl mb-3 font-extralight tracking-tight drag-handler"
      >
        {{ this.new === true ? 'New Product' : 'Edit Product' }}
      </div>

      <form @submit.prevent="handleConfirm" autocomplete="off">
        <fieldset :disabled="!isAdmin" class="border-0 p-0 m-0">
        <label
          class="mt-4 block text-left uppercase tracking-wide text-gray-700 text-xs font-bold"
        >
          Ordering Display Name
          <input
            class="focus:outline-none focus:shadow-outline appearance-none block w-full text-base font-normal bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight focus:bg-white focus:border-gray-500 mt-2"
            type="text"
            placeholder="(e.g. Spoons)"
            v-model.lazy="product.name"
            autocomplete="off"
          />
        </label>

        <label
          class="mt-4 block text-left uppercase tracking-wide text-gray-700 text-xs font-bold"
          for="genericProduct"
        >
          Generic Product
        </label>
        <div class="mt-2">
          <div class="flex items-center">
            <div class="relative w-full">
              <select
                class="block appearance-none w-full bg-gray-200 border border-gray-200 text-gray-700 py-3 px-4 pr-8 rounded leading-tight focus:outline-none focus:shadow-outline focus:bg-white focus:border-gray-500"
                id="genericProduct"
                v-model="product.genericProductId"
                @click.stop
              >
                <option value="" disabled selected>
                  selection required...
                </option>
                <option
                  v-for="id in sortedGenericProductIds"
                  :key="`${id}-genericProducts`"
                  :value="id"
                >
                  {{
                    `${genericProducts[id].name}${
                      genericProductIdsWithoutLocalIds.includes(id)
                        ? ' (missing)'
                        : ''
                    }` | capitalize
                  }}
                </option>
              </select>
              <div
                class="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700"
              >
                <svg
                  class="fill-current h-4 w-4"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                >
                  <path
                    d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"
                  />
                </svg>
              </div>
            </div>
            <button
              v-if="product.genericProductId"
              @click="fillFromGenericProduct(product.genericProductId)"
              class="ml-2 px-3 py-3 rounded bg-gray-300 hover:bg-gray-400 focus:outline-none focus:shadow-outline"
              type="button"
              title="Auto-fill fields from generic product"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-5 w-5"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fill-rule="evenodd"
                  d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z"
                  clip-rule="evenodd"
                />
              </svg>
            </button>
          </div>
        </div>

        <label
          class="mt-4 block text-left uppercase tracking-wide text-gray-700 text-xs font-bold"
        >
          ID
          <input
            class="focus:outline-none focus:shadow-outline appearance-none block w-full text-base font-normal bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight focus:bg-white focus:border-gray-500 mt-2"
            type="text"
            placeholder="(e.g. Spoons)"
            v-model.lazy="product.id"
            autocomplete="off"
            disabled
          />
        </label>

        <label
          class="mt-4 block text-left uppercase tracking-wide text-gray-700 text-xs font-bold"
        >
          Invoice Name
          <input
            class="text-base font-normal focus:outline-none focus:shadow-outline appearance-none block w-full bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight focus:outline-none focus:bg-white focus:border-gray-500 mt-2"
            type="text"
            placeholder="(e.g. Spoons, black plastic.)"
            v-model.lazy="product.details"
            autocomplete="off"
          />
        </label>

        <label
          class="mt-4 block text-left uppercase tracking-wide text-gray-700 text-xs font-bold"
        >
          Invoice Code
          <input
            class="text-base font-normal focus:outline-none focus:shadow-outline appearance-none block w-full bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight focus:outline-none focus:bg-white focus:border-gray-500 mt-2"
            type="text"
            placeholder="(e.g. X-1000002RY)"
            v-model.lazy="product.code"
            autocomplete="off"
          />
        </label>

        <label
          class="mt-4 block text-left uppercase tracking-wide text-gray-700 text-xs font-bold"
        >
          Order Unit (OU)
          <span
            v-if="selectedGenericProduct && selectedGenericProduct.unit"
            class="float-right"
            :class="{
              'text-gray-500': product.unit === selectedGenericProduct.unit,
              'text-yellow-500':
                product.unit !== selectedGenericProduct.unit &&
                product.unit &&
                selectedGenericProduct.unit
            }"
          >
            ({{ selectedGenericProduct.unit }})
          </span>
          <input
            class="text-base font-normal focus:outline-none focus:shadow-outline appearance-none block w-full bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight focus:outline-none focus:bg-white focus:border-gray-500 mt-2"
            type="text"
            placeholder="(e.g. Case)"
            v-model.lazy="product.unit"
            autocomplete="off"
          />
        </label>

        <label
          class="mt-4 block text-left uppercase tracking-wide text-gray-700 text-xs font-bold"
        >
          Counting Unit (CU)
          <span
            v-if="selectedGenericProduct && selectedGenericProduct.unitCounting"
            class="float-right"
            :class="{
              'text-gray-500':
                product.unitCounting === selectedGenericProduct.unitCounting,
              'text-yellow-500':
                product.unitCounting !== selectedGenericProduct.unitCounting &&
                product.unitCounting &&
                selectedGenericProduct.unitCounting
            }"
          >
            ({{ selectedGenericProduct.unitCounting }})
          </span>
          <input
            class="text-base font-normal focus:outline-none focus:shadow-outline appearance-none block w-full bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight focus:outline-none focus:bg-white focus:border-gray-500 mt-2"
            type="text"
            placeholder="(e.g. Box)"
            v-model.lazy="product.unitCounting"
            autocomplete="off"
          />
        </label>

        <label
          class="mt-4 block text-left uppercase tracking-wide text-gray-700 text-xs font-bold"
        >
          Question
          <input
            class="text-base font-normal focus:outline-none focus:shadow-outline appearance-none block w-full bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight focus:outline-none focus:bg-white focus:border-gray-500 mt-2"
            type="text"
            placeholder="(e.g. How many boxes are there?)"
            v-model.lazy="product.question"
            autocomplete="off"
          />
        </label>

        <label
          class="mt-4 block text-left uppercase tracking-wide text-gray-700 text-xs font-bold"
        >
          Counting Instructions
          <textarea
            class="text-base font-normal focus:outline-none focus:shadow-outline appearance-none block w-full bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight focus:outline-none focus:bg-white focus:border-gray-500 mt-2"
            type="text"
            placeholder=""
            v-model.lazy="product.instructions"
            autocomplete="off"
          />
        </label>

        <label
          class="mt-4 block text-left uppercase tracking-wide text-gray-700 text-xs font-bold"
          for="countingUnits"
        >
          Counting Units in each Order Unit
          <span
            v-if="selectedGenericProduct && selectedGenericProduct.qtyPerUnit"
            class="float-right"
            :class="{
              'text-gray-500':
                product.qtyPerUnit === selectedGenericProduct.qtyPerUnit,
              'text-yellow-500':
                product.qtyPerUnit !== selectedGenericProduct.qtyPerUnit &&
                product.qtyPerUnit &&
                selectedGenericProduct.qtyPerUnit
            }"
          >
            ({{ selectedGenericProduct.qtyPerUnit }})
          </span>
        </label>

        <div class="mt-2">
          <div class="relative">
            <select
              class="block appearance-none w-full bg-gray-200 border border-gray-200 text-gray-700 py-3 px-4 pr-8 rounded leading-tight focus:outline-none focus:shadow-outline focus:bg-white focus:border-gray-500"
              id="countingUnits"
              v-model="product.qtyPerUnit"
              @click.stop
            >
              <option value="" disabled selected>selection required...</option>
              <option
                v-for="i in Array.from({ length: 100 }, (v, k) => k + 1)"
                :key="`${i}-countingUnits`"
                :value="i"
              >
                {{ i }}
              </option>
            </select>
            <div
              class="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700"
            >
              <svg
                class="fill-current h-4 w-4"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
              >
                <path
                  d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"
                />
              </svg>
            </div>
          </div>
        </div>

        <label
          class="mt-4 block text-left uppercase tracking-wide text-gray-700 text-xs font-bold"
          for="par"
        >
          Par Amount
          <span
            v-if="selectedGenericProduct && selectedGenericProduct.par"
            class="float-right"
            :class="{
              'text-gray-500': product.par === selectedGenericProduct.par,
              'text-yellow-500':
                product.par !== selectedGenericProduct.par &&
                product.par &&
                selectedGenericProduct.par
            }"
          >
            ({{ selectedGenericProduct.par }})
          </span>
        </label>
        <div class="mt-2">
          <div class="relative">
            <select
              class="block appearance-none w-full bg-gray-200 border border-gray-200 text-gray-700 py-3 px-4 pr-8 rounded leading-tight focus:outline-none focus:shadow-outline focus:bg-white focus:border-gray-500"
              id="par"
              v-model="product.par"
              @click.stop
            >
              <option value="" disabled selected>selection required...</option>
              <option
                v-for="i in Array.from({ length: 150 }, (v, k) => k + 1)"
                :key="`${i}-categorys`"
                :value="i"
              >
                {{ i }}
              </option>
            </select>
            <div
              class="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700"
            >
              <svg
                class="fill-current h-4 w-4"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
              >
                <path
                  d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"
                />
              </svg>
            </div>
          </div>
        </div>

        <label
          class="mt-4 block text-left uppercase tracking-wide text-gray-700 text-xs font-bold"
          for="response"
        >
          Response Format
          <span
            v-if="selectedGenericProduct && selectedGenericProduct.inputType"
            class="float-right"
            :class="{
              'text-gray-500':
                product.inputType === selectedGenericProduct.inputType,
              'text-yellow-500':
                product.inputType !== selectedGenericProduct.inputType &&
                product.inputType &&
                selectedGenericProduct.inputType
            }"
          >
            ({{ inputTypes[selectedGenericProduct.inputType] | capitalize }})
          </span>
        </label>
        <div class="mt-2">
          <div class="relative">
            <select
              class="block appearance-none w-full bg-gray-200 border border-gray-200 text-gray-700 py-3 px-4 pr-8 rounded leading-tight focus:outline-none focus:shadow-outline focus:bg-white focus:border-gray-500"
              id="response"
              v-model="product.inputType"
              @click.stop
            >
              <option value="" disabled selected>selection required...</option>
              <option
                v-for="i in Object.keys(inputTypes)"
                :key="`${i}-inputTypes`"
                :value="i"
              >
                {{ inputTypes[i] | capitalize }}
              </option>
            </select>
            <div
              class="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700"
            >
              <svg
                class="fill-current h-4 w-4"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
              >
                <path
                  d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"
                />
              </svg>
            </div>
          </div>
        </div>

        <transition-expand>
          <label
            class="mt-4 block text-left uppercase tracking-wide text-gray-700 text-xs font-bold"
            v-if="product.inputType && product.inputType !== 'number'"
          >
            Inventory Question
            <input
              class="text-base font-normal focus:outline-none focus:shadow-outline appearance-none block w-full bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight focus:outline-none focus:bg-white focus:border-gray-500 mt-2"
              type="text"
              placeholder="(e.g. How many boxes are there?)"
              v-model.lazy="product.inventoryQuestion"
              autocomplete="off"
            />
          </label>
        </transition-expand>

        <label
          class="mt-4 block text-left uppercase tracking-wide text-gray-700 text-xs font-bold"
          for="vendor"
        >
          Vendor
        </label>
        <div class="mt-2">
          <div class="relative">
            <select
              class="block appearance-none w-full bg-gray-200 border border-gray-200 text-gray-700 py-3 px-4 pr-8 rounded leading-tight focus:outline-none focus:shadow-outline focus:bg-white focus:border-gray-500"
              id="vendor"
              v-model="product.vendor"
              @click.stop
            >
              <option value="" disabled selected>selection required...</option>
              <option
                v-for="i in Object.keys(vendors)"
                :key="`${i}-vendors`"
                :value="i"
              >
                {{ vendors[i].name | capitalize }}
              </option>
            </select>
            <div
              class="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700"
            >
              <svg
                class="fill-current h-4 w-4"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
              >
                <path
                  d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"
                />
              </svg>
            </div>
          </div>
        </div>

        <label
          class="mt-4 block text-left uppercase tracking-wide text-gray-700 text-xs font-bold"
        >
          Recipe Unit (RU)
          <span
            v-if="selectedGenericProduct && selectedGenericProduct.unitRecipe"
            class="float-right"
            :class="{
              'text-gray-500':
                product.unitRecipe === selectedGenericProduct.unitRecipe,
              'text-yellow-500':
                product.unitRecipe !== selectedGenericProduct.unitRecipe &&
                product.unitRecipe &&
                selectedGenericProduct.unitRecipe
            }"
          >
            ({{ selectedGenericProduct.unitRecipe }})
          </span>
          <input
            class="text-base font-normal focus:outline-none focus:shadow-outline appearance-none block w-full bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight focus:outline-none focus:bg-white focus:border-gray-500 mt-2"
            type="text"
            placeholder="(e.g. Box)"
            v-model.lazy="product.unitRecipe"
            autocomplete="off"
          />
        </label>

        <label
          class="mt-4 block text-left uppercase tracking-wide text-gray-700 text-xs font-bold"
        >
          Recipe Unit in each Order Unit
          <span
            v-if="
              selectedGenericProduct &&
              selectedGenericProduct.recipeUnitPerOrderUnit
            "
            class="float-right"
            :class="{
              'text-gray-500':
                product.recipeUnitPerOrderUnit ===
                selectedGenericProduct.recipeUnitPerOrderUnit,
              'text-yellow-500':
                product.recipeUnitPerOrderUnit !==
                  selectedGenericProduct.recipeUnitPerOrderUnit &&
                product.recipeUnitPerOrderUnit &&
                selectedGenericProduct.recipeUnitPerOrderUnit
            }"
          >
            ({{ selectedGenericProduct.recipeUnitPerOrderUnit }})
          </span>
          <input
            class="text-base font-normal focus:outline-none focus:shadow-outline appearance-none block w-full bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight focus:outline-none focus:bg-white focus:border-gray-500 mt-2"
            type="tel"
            placeholder="(e.g. 100)"
            v-model.lazy="product.recipeUnitPerOrderUnit"
            autocomplete="off"
          />
        </label>

        <label
          class="mt-4 block text-left uppercase tracking-wide text-gray-700 text-xs font-bold"
        >
          Yield Percent
          <span
            v-if="selectedGenericProduct && selectedGenericProduct.yieldPercent"
            class="float-right"
            :class="{
              'text-gray-500':
                product.yieldPercent == selectedGenericProduct.yieldPercent,
              'text-yellow-500':
                product.yieldPercent != selectedGenericProduct.yieldPercent &&
                product.yieldPercent &&
                selectedGenericProduct.yieldPercent
            }"
          >
            ({{ selectedGenericProduct.yieldPercent }}%)
          </span>
          <div
            class="mt-2 text-center text-base font-normal normal-case focus:outline-none focus:shadow-outline appearance-none block bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4 pr-10 leading-tight focus:outline-none focus:bg-white focus:border-gray-500"
          >
            <input
              class="appearance-none bg-transparent text-center leading-tight w-full"
              type="tel"
              autocomplete="off"
              v-model="product.yieldPercent"
            />
            <div
              class="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-gray-500 font-bold"
            >
              %
            </div>
          </div>
        </label>

        <label
          class="mt-4 block text-left uppercase tracking-wide text-gray-700 text-xs font-bold"
          for="category"
        >
          Expense Category
          <span
            v-if="selectedGenericProduct && selectedGenericProduct.category"
            class="float-right"
            :class="{
              'text-gray-500':
                product.category === selectedGenericProduct.category,
              'text-yellow-500':
                product.category !== selectedGenericProduct.category &&
                product.category &&
                selectedGenericProduct.category
            }"
          >
            ({{ selectedGenericProduct.category | capitalize }})
          </span>
        </label>
        <div class="mt-2">
          <div class="relative">
            <select
              class="block appearance-none w-full bg-gray-200 border border-gray-200 text-gray-700 py-3 px-4 pr-8 rounded leading-tight focus:outline-none focus:shadow-outline focus:bg-white focus:border-gray-500"
              id="category"
              v-model="product.category"
              @click.stop
            >
              <option value="" disabled selected>selection required...</option>
              <option
                v-for="i in productExpenseCategories"
                :key="`${i}-categorys`"
                :value="i"
              >
                {{ i | capitalize }}
              </option>
            </select>
            <div
              class="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700"
            >
              <svg
                class="fill-current h-4 w-4"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
              >
                <path
                  d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"
                />
              </svg>
            </div>
          </div>
        </div>

        <label
          class="mt-4 block text-left uppercase tracking-wide text-gray-700 text-xs font-bold"
          for="location"
        >
          Location
          <span
            v-if="selectedGenericProduct && selectedGenericProduct.location"
            class="float-right"
            :class="{
              'text-gray-500':
                product.location === selectedGenericProduct.location,
              'text-yellow-500':
                product.location !== selectedGenericProduct.location &&
                product.location &&
                selectedGenericProduct.location
            }"
          >
            ({{ selectedGenericProduct.location | capitalize }})
          </span>
        </label>
        <div class="mt-2">
          <div class="relative">
            <select
              class="block appearance-none w-full bg-gray-200 border border-gray-200 text-gray-700 py-3 px-4 pr-8 rounded leading-tight focus:outline-none focus:shadow-outline focus:bg-white focus:border-gray-500"
              id="location"
              v-model="product.location"
              @click.stop
            >
              <option value="" disabled selected>selection optional...</option>
              <option
                v-for="i in productLocations"
                :key="`${i}-locations`"
                :value="i"
              >
                {{ i | capitalize }}
              </option>
            </select>
            <div
              class="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700"
            >
              <svg
                class="fill-current h-4 w-4"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
              >
                <path
                  d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"
                />
              </svg>
            </div>
          </div>
        </div>

        <div class="mt-6 flex flex-row justify-between text-lg text-gray-900">
          <span>Is this product currently active? </span>
          <s-switch class="" v-model="product.active" :disabled="!isAdmin" />
        </div>

        <div class="mt-6 flex flex-row justify-between text-lg text-gray-900">
          <span>Can this product be transferred? </span>
          <s-switch
            class=""
            v-model="product.transferable"
            :disabled="true"
            :title="'This setting can only be changed at the global product level'"
          />
        </div>
        <div class="text-sm text-gray-500 text-right -mt-1">
          (Set at global level)
        </div>

        <label
          class="mt-4 block text-left uppercase tracking-wide text-gray-700 text-xs font-bold"
        >
          Current Item Cost
          <money
            class="text-base font-normal focus:outline-none focus:shadow-outline appearance-none block w-full bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight focus:outline-none focus:bg-white focus:border-gray-500 mt-2"
            v-model.lazy="productPrice"
            v-bind="moneyConfig"
            autocomplete="off"
          />
        </label>

        <label
          class="mt-4 block text-left uppercase tracking-wide text-gray-700 text-xs font-bold"
        >
          Recipe Unit Price
          <div
            class="text-base text-center font-normal focus:outline-none focus:shadow-outline appearance-none block w-full bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight focus:outline-none focus:bg-white focus:border-gray-500 mt-2"
          >
            {{ productRecipeUnitPriceByProductId(product.id) | usd }}
          </div>
        </label>

        </fieldset>
        <div class="mt-6 flex flex-row justify-between">
          <button
            class="buttonOutline focus:shadow-outline focus:outline-none"
            type="button"
            @click="handleCancel"
          >
            Cancel
          </button>
          <button
            class="ml-3 buttonOutline focus:shadow-outline focus:outline-none"
            v-if="!this.new && isAdmin"
            type="button"
            @click="handleDelete"
          >
            Delete
          </button>
          <button
            v-if="isAdmin"
            class="ml-3 button focus:shadow-outline focus:outline-none"
            type="submit"
          >
            {{ this.new === true ? 'Create' : 'Save' }}
          </button>
        </div>
      </form>
    </div>
  </modal>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import TransitionExpand from '@/components/TransitionExpand'
import { inputTypes } from '@/const.js'
import SSwitch from '@/components/SSwitch.vue'
import { Money } from 'v-money'
import { floatify } from '@/utils'

export default {
  name: 'ModalEditProduct',
  data() {
    return {
      inputTypes,
      new: null,
      originalProduct: null,
      productPrice: 0,
      product: {
        id: null,
        active: true,
        name: null,
        details: null,
        code: null,
        unit: null,
        unitOrdering: null,
        unitCounting: null,
        question: null,
        instructions: null,
        inventoryQuestion: null,
        qtyPerUnit: null,
        category: null,
        location: null,
        vendor: null,
        inputType: null,
        par: null,
        genericProductId: null,
        transferable: true // Default to true
      },
      moneyConfig: {
        decimal: '.',
        thousands: ',',
        prefix: '$ ',
        suffix: '',
        precision: 2,
        masked: false /* doesn't work with directive */
      }
    }
  },
  components: {
    TransitionExpand,
    SSwitch,
    Money
  },
  computed: {
    ...mapGetters('storeModule/vendors', ['vendors']),
    ...mapGetters('storeModule/products', ['products']),
    ...mapGetters('storeModule', [
      'productPriceByProductId',
      'productRecipeUnitPriceByProductId'
    ]),
    ...mapGetters('global', {
      productLocations: 'settingsProductLocations',
      productExpenseCategories: 'settingsProductExpenseCategories'
    }),
    ...mapGetters('user', ['isAdmin', 'isManager']),
    ...mapGetters({
      genericProducts: 'global/products'
    }),
    ...mapGetters('global', ['genericProductIdsWithoutLocalIds']),
    sortedGenericProductIds() {
      if (!this.genericProducts) return []

      return Object.keys(this.genericProducts).sort((a, b) => {
        const nameA = this.genericProducts[a].name
          ? this.genericProducts[a].name.toLowerCase()
          : ''
        const nameB = this.genericProducts[b].name
          ? this.genericProducts[b].name.toLowerCase()
          : ''
        return nameA.localeCompare(nameB)
      })
    },
    selectedGenericProduct() {
      if (!this.genericProducts || !this.product.genericProductId) return null
      return this.genericProducts[this.product.genericProductId] || null
    }
  },
  watch: {
    'product.genericProductId': function () {
      // Auto-fill disabled by default to prevent overriding existing data
      // if (newValue && newValue !== oldValue) {
      //   this.fillFromGenericProduct(newValue)
      // }
    }
  },
  methods: {
    ...mapActions('config', ['setFabVisible', 'setDragLock']),
    ...mapActions('storeModule/products', { updateProduct: 'updateItem' }),
    ...mapActions('storeModule', {
      createProduct: 'createProduct',
      updateStore: 'updateItem',
      deleteProduct: 'deleteProduct',
      updateProductPrice: 'updateProductPrice'
    }),
    floatify,
    hideModal() {
      this.$modal.hide('modal--edit-product')
    },
    beforeOpen(event) {
      // If an ID for a product was passed, get that product info, if not, set blank
      if (
        event &&
        event.params &&
        event.params.id &&
        this.products[event.params.id]
      ) {
        this.new = false
        // create a local copy, not a reference, for temporary editing
        this.product = {
          ...this.products[event.params.id]
        }
        // Set default for transferable if it doesn't exist
        if (
          !Object.prototype.hasOwnProperty.call(this.product, 'transferable')
        ) {
          this.product.transferable = true
        }
        try {
          this.productPrice = this.productPriceByProductId(event.params.id).cost
        } catch {
          this.productPrice = 0
        }
        if (!Object.prototype.hasOwnProperty.call(this.product, 'active'))
          this.product.active = true
      } else {
        this.new = true
        this.product.active = true
        this.product.transferable = true // Default for new products
      }

      // not using Vue.set since this doesn't need to be reactive? wrong..
      this.originalProduct = { ...this.product }
      this.setFabVisible(false)
      this.setDragLock(true)
    },
    beforeClose() {
      this.originalProduct = {}
      Object.keys(this.product).forEach((property) => {
        this.product[property] = null
      })
      this.setFabVisible(true)
      this.setDragLock(false)
    },
    handleDelete() {
      this.deleteProduct(this.product.id)
      this.hideModal()
    },
    handleCancel() {
      this.hideModal()
    },
    handleConfirm() {
      // Remove validation that prevents multiple products with same genericProductId
      // -- This allows different vendor-specific variations of the same generic product

      // if category is blank and we have a generic product, use its category
      if (
        !this.product.category &&
        this.product.genericProductId &&
        this.genericProducts[this.product.genericProductId]
      ) {
        this.product.category =
          this.genericProducts[this.product.genericProductId].category
      }

      // add: object comparison to determine if update actually needs pushed
      if (this.new) {
        this.createProduct({ ...this.product }).then(() =>
          this.updateProductPrice({
            id: this.product.id,
            cost: this.productPrice
          })
        )
      } else {
        this.updateProduct({ id: this.product.id, data: { ...this.product } })
        this.updateProductPrice({
          id: this.product.id,
          cost: this.productPrice
        })
      }
      this.hideModal()
    },
    fillFromGenericProduct(genericProductId) {
      // Get the generic product data
      if (!this.genericProducts || !genericProductId) return

      const genericProduct = this.genericProducts[genericProductId]
      if (!genericProduct) return

      // Only auto-fill fields that are empty or if this is a new product
      if (this.new || !this.product.name) {
        this.product.name = genericProduct.name || this.product.name
      }

      // Auto-fill other fields if they exist in the generic product and are empty in the current product
      const fieldsToAutoFill = [
        'unit',
        'unitCounting',
        'unitRecipe',
        'category',
        'recipeUnitPerOrderUnit',
        'yieldPercent',
        'par',
        'inputType',
        'location',
        // new
        'qtyPerUnit',
        'calculationType',
        'question',
        'instructions',
        'inventoryQuestion',
        'details',
        'name'
      ]

      fieldsToAutoFill.forEach((field) => {
        if (genericProduct[field]) {
          // && (!this.product[field] || this.new)) {
          this.product[field] = genericProduct[field]
        }
      })
    }
  }
}
</script>
